import {
    createContext,
    useState,
    useEffect,
    useContext,
    useCallback,
} from "react";
import { ID } from "appwrite";
import { account, functions } from "../appwrite/appwriteConfig";
import Loading from "./Loading";
import { getBaseUrl } from "./getRedirectUrl";

const AuthContext = createContext();

// TO BE UPDATED WITH OAKMATE.APP/*

export const AuthProvider = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [authError, setAuthError] = useState(null);
    const [license, setLicense] = useState("Loading...");
    const [nextPayment, setNextPayment] = useState(null);
    const [isTrialExpired, setIsTrialExpired] = useState(true);

    useEffect(() => {
        //setLoading(false)
        checkUserStatus();
        // eslint-disable-next-line
    }, []);

    const loginUser = async (userInfo) => {
        setLoading(true);
        setAuthError(null); // Reset error on new attempt

        try {
            let response = await account.createEmailPasswordSession(
                userInfo.email,
                userInfo.password
            );
            let accountDetails = await account.get();
            setUser(accountDetails);
            setAuthError(null);
            return { success: true, response };
        } catch (error) {
            setAuthError(error.message); // Set global error
            return { success: false, error };
        } finally {
            setLoading(false);
        }
    };

    const logoutUser = async () => {
        await account.deleteSession("current");
        setUser(null);
    };

    const registerUser = async (userInfo) => {
        setLoading(true);

        try {
            // create account
            await account.create(
                ID.unique(),
                userInfo.email,
                userInfo.password
            );

            // console.log("Account created:", response);

            console.log("trying to create session");
            await account.createEmailPasswordSession(
                userInfo.email,
                userInfo.password
            );

            let accountDetails = await account.get();
            setUser(accountDetails);
            setAuthError(null);

            return true;
        } catch (error) {
            console.error("Full error details:", {
                message: error.message,
                type: error.type,
                code: error.code,
                response: error.response, // Appwrite-specific details
            });
            setAuthError(error.message);

            return false;
        } finally {
            setLoading(false);
        }
    };

    const sendVerification = async () => {
        setLoading(true);
        try {
            await account.createVerification(`${getBaseUrl()}/confirm`);
            // console.log("Verification response:", response);
        } catch (error) {
            setAuthError(error?.message || "An unknown error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const isUserOlderThan12Hours = useCallback((userData) => {
        if (!userData || !userData.$createdAt) return false;

        const userCreationTime = new Date(userData.$createdAt).getTime();
        const oneHoursAgo = Date.now() - 1 * 60 * 60 * 1000;

        return userCreationTime < oneHoursAgo;
    }, []);

    const sendOnboardingEmail = useCallback(async () => {
        setLoading(true);
        try {
            // Always get the latest user data directly
            const currentUser = user || (await account.get());

            // Update state if we fetched the user
            if (!user) {
                setUser(currentUser);
            }

            if (isUserOlderThan12Hours(currentUser)) {
                setLoading(false);
                // console.log(
                //     "User is older than 1 hour, not sending onboarding email."
                // );
                return;
            }

            // console.log("Google user:", currentUser);

            // Trigger send-email function
            await functions.createExecution(
                "686cd8530014febec91b",
                JSON.stringify({
                    email: currentUser.email,
                    username: currentUser.name || null,
                }),
                false,
                "/send-email",
                "POST"
            );
        } catch (error) {
            setAuthError(error?.message || "An unknown error occurred.");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line
    }, [user, setUser, setLoading, setAuthError]);

    const sendRecovery = async (email) => {
        try {
            const response = await account.createRecovery(
                email,
                `${getBaseUrl()}/recovery`
            );
            return response; // Return response for success handling
        } catch (error) {
            // Throw error to be caught by component
            throw new Error(error?.message || "An unknown error occurred.");
        }
    };

    const cancelSubscription = async () => {
        setLoading(true);
        try {
            // 1. First call the cloud function while still authenticated
            const execution = await functions.createExecution(
                "686cd8530014febec91b",
                JSON.stringify({ userId: user.$id }),
                false, // Make it synchronous
                "/cancel-subscription",
                "POST"
            );

            // 2. Check if cloud function succeeded
            const response = JSON.parse(execution.responseBody);

            if (!response.success) {
                throw new Error(
                    "Cloud function failed: " +
                        (response.error || "Unknown error")
                );
            }

            setLicense("free");
            setNextPayment(null);

            return true;
        } catch (error) {
            window.alert(
                "An error occurred while canceling your subscription. Please try again."
            );
            console.error("Subscription cancelation failed:", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const deleteAccount = async () => {
        setLoading(true);
        try {
            // 1. First call the cloud function while still authenticated
            const execution = await functions.createExecution(
                "686cd8530014febec91b",
                JSON.stringify({ userId: user.$id }),
                false, // Make it synchronous
                "/delete-user",
                "POST"
            );

            // 2. Check if cloud function succeeded
            const response = JSON.parse(execution.responseBody);

            if (!response.success) {
                throw new Error(
                    "Cloud function failed: " +
                        (response.error || "Unknown error")
                );
            }

            // 3. Only clear local state AFTER successful deletion
            setUser(null);
            try {
                await account.deleteSession("current");
            } catch (sessionError) {
                console.warn("Session deletion warning:", sessionError);
            }

            return true;
        } catch (error) {
            console.error("Account deletion failed:", error);
            window.alert(
                "An error occurred while deleting your account. Please try again."
            );
            return false;
        } finally {
            setLoading(false);
        }
    };

    const setChesscom = async (username) => {
        if (!user?.$id) {
            console.warn("setChesscom: user not authenticated");
            return false;
        }

        setLoading(true);
        try {
            // 1. Check if player exists
            const response = await fetch(
                `https://api.chess.com/pub/player/${username}`
            );
            // console.log("response from chess api", response);
            if (!response.ok) {
                // Player does not exist
                return false;
            }
            const playerData = await response.json();

            // 2. Fetch current preferences
            const currentPrefs = await account.getPrefs?.();

            // 3. Build merged prefs with chesscom username and avatar
            const merged = {
                darkTheme: currentPrefs?.darkTheme ?? true,
                language: currentPrefs?.language ?? "en",
                ...currentPrefs,
                chesscom: username,
                chesscomAvatar: playerData.avatar || null,
            };

            // 4. Update with merged preferences
            const updated = await account.updatePrefs(merged);
            setUser(updated); // update state with new user object

            return true;
        } catch (error) {
            console.error("Failed to setChesscom:", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const checkUserStatus = useCallback(async () => {
        try {
            let accountDetails = await account.get();
            setUser(accountDetails);
        } catch (error) {
            setUser(null);
        }
        setLoading(false);
    }, [setUser, setLoading]);

    const getLicense = async () => {
        const response = await functions.createExecution(
            "686cd8530014febec91b",
            JSON.stringify({}),
            false,
            "/get-subscription",
            "GET"
        );

        if (response.responseBody) {
            try {
                const parsed = JSON.parse(response.responseBody);
                setLicense(parsed?.plan ?? "free");

                if (parsed.amount && parsed.nextPaymentDate) {
                    setNextPayment({
                        amount: parsed.amount,
                        currency: parsed.currency,
                        date: parsed.nextPaymentDate,
                    });
                }

                if (
                    parsed?.plan === "masterMonthly" ||
                    parsed?.plan === "masterYearly"
                ) {
                    setIsTrialExpired(false);
                    return true;
                }

                if (parsed?.plan === "free" && user?.$createdAt) {
                    const createdAt = new Date(user.$createdAt);
                    const now = new Date();
                    const diffDays = (now - createdAt) / (1000 * 60 * 60 * 24);
                    if (diffDays < 7) {
                        setIsTrialExpired(false);
                        return false; // User is within the 7-day trial period
                    }
                }

                return false;
            } catch (e) {
                setLicense("free");
                setIsTrialExpired(true);
                return false;
            }
        } else {
            setLicense("free");
            setIsTrialExpired(true);
            return false;
        }
    };

    const contextData = {
        user,
        authError, // Expose error in context
        setAuthError, // Expose setter
        loginUser,
        logoutUser,
        registerUser,
        sendVerification,
        checkUserStatus,
        sendRecovery,
        getLicense,
        license,
        isTrialExpired,
        nextPayment,
        deleteAccount,
        cancelSubscription,
        sendOnboardingEmail,
        ...(user && { setChesscom }),
    };

    return (
        <AuthContext.Provider value={contextData}>
            {loading ? <Loading /> : children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
